import { useEffect, useMemo, useState } from 'react';
import { MicButton, appendSpoken } from '@/components/ui/MicButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, MapPin, Keyboard, ScanLine, Upload, Check, Locate } from 'lucide-react';
import { api, uploadFile } from '@/api/client';
import { toCommaList } from '@/utils/search-list';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

interface OrderStatus { id: number; name: string; code?: string; category?: string }
interface UserOpt { id: number; name?: string; email: string; mobile?: string }

interface LookupItem {
  input: string;
  exists: boolean;
  orderId?: string; orderNo?: string; orderType?: 'B2C' | 'B2B';
  awbNo?: string; lrNo?: string; statusId?: number;
  customerName?: string; customerMobile?: string;
  courier?: { id: number; name: string; code: string };
  merchant?: { id: number; companyName: string };
  // B2B child-box scan metadata — present only when the scanned value was a child AWB.
  isChild?: boolean; childAwb?: string; boxNo?: number; totalBoxes?: number;
}

interface ScanRow {
  input: string;
  found: LookupItem;
  podFile?: string;        // base64 data URL
  podFileName?: string;
  receiverName?: string;
  receiverPhone?: string;
  // Per-AWB weight + dimensions (override the shared defaults below). Kept as strings for the inputs.
  weight?: string;
  lengthCm?: string;
  widthCm?: string;
  heightCm?: string;
}

interface Props {
  orderType: 'B2C' | 'B2B';
  onClose: () => void;
  onSuccess?: () => void;
  prefilledAwb?: string;
}

// Statuses that need POD + receiver capture
const POD_REQUIRED_STATUSES = new Set<number>([8, 11, 12, 19]);

export function ScanDialog({ orderType, onClose, onSuccess, prefilledAwb }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'manual' | 'qr'>('manual');
  const [statusId, setStatusId] = useState<number>(0);
  const [assignedUserId, setAssignedUserId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');
  const [awbInput, setAwbInput] = useState(prefilledAwb ?? '');
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [chargedWeight, setChargedWeight] = useState<string>('');
  const [lengthCm, setLengthCm] = useState<string>('');
  const [widthCm, setWidthCm] = useState<string>('');
  const [heightCm, setHeightCm] = useState<string>('');
  const [ndrReason, setNdrReason] = useState<number | ''>('');

  // Location
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const { data: statuses } = useQuery({
    queryKey: ['order-statuses'],
    queryFn: async () => {
      try {
        const r: any = (await api.get('/settings/order_statuses')).data;
        const list = Array.isArray(r) ? (r as OrderStatus[]) : ((r?.items ?? []) as OrderStatus[]);
        // Statuses the status master marks as RTO-module-only (warehouse receipt, merchant handover) are
        // recorded from RTO Orders -> Scan RTO, which checks the transition and demands proof.
        return list.filter((s: any) => !s?.rtoModuleOnly);
      } catch { return [] as OrderStatus[]; }
    },
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: async () => {
      try {
        // Lightweight assignable-users list (open to operational staff, not just admins).
        const r: any = (await api.get('/users/assignable')).data;
        return Array.isArray(r) ? (r as UserOpt[]) : ((r?.items ?? []) as UserOpt[]);
      } catch { return [] as UserOpt[]; }
    },
  });


  const { data: ndrStatuses } = useQuery({
    queryKey: ['ndr-statuses'],
    queryFn: async () => {
      try {
        const r: any = (await api.get('/settings/ndr_statuses')).data;
        return Array.isArray(r) ? r : (r?.items ?? []);
      } catch { return []; }
    },
  });

  useEffect(() => { getLocation(); /* eslint-disable-next-line */ }, []);

  async function getLocation() {
    setGeoError(null); setGeoLoading(true);
    if (!('geolocation' in navigator)) {
      setGeoError('Geolocation not supported by this browser');
      setGeoLoading(false); return;
    }
    try {
      const pos = await new Promise<GeolocationPosition>((res, rej) => {
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 });
      });
      const { latitude, longitude } = pos.coords;
      setLat(latitude); setLng(longitude);
      try {
        const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
        const j = await r.json();
        setCity(j.city || j.locality || j.principalSubdivision || '');
        setState(j.principalSubdivision || '');
        setPincode(j.postcode || '');
      } catch (_) { /* swallow */ }
    } catch (e: any) {
      setGeoError(e?.message ?? 'Location permission denied — click "Get Location" to retry');
    } finally { setGeoLoading(false); }
  }

  // Lookup AWBs (comma-separated) on ADD
  const lookupMut = useMutation({
    mutationFn: async (awbsRaw: string) => {
      const parts = awbsRaw.split(/[\s,;|]+/).map(s => s.trim()).filter(Boolean);
      const dedup = Array.from(new Set(parts.filter(a => !rows.find(r => r.found.awbNo === a || r.found.lrNo === a || r.found.input === a))));
      if (dedup.length === 0) throw new Error('No new AWBs to add');
      if (rows.length + dedup.length > 200) throw new Error(`Max 200 AWBs (you have ${rows.length}, adding ${dedup.length})`);
      return (await api.post<{ items: LookupItem[] }>('/tracking/lookup', { awbs: dedup })).data;
    },
    onSuccess: (data) => {
      const newRows: ScanRow[] = data.items.map(it => ({ input: it.input, found: it }));
      // Newest scan first — the scanner is looking at what they just scanned, not the top of a long list.
      setRows(prev => [...newRows, ...prev]);
      const missing = data.items.filter(it => !it.exists);
      if (missing.length > 0) toast.error(`${missing.length} AWB(s) not found in database`);
      const found = data.items.length - missing.length;
      if (found > 0) toast.success(`Added ${found} AWB(s)`);
      setAwbInput('');
    },
    onError: (e: any) => toast.error(e?.message ?? 'Lookup failed'),
  });

  function removeRow(input: string) {
    setRows(prev => prev.filter(r => r.input !== input));
  }
  function updateRow(input: string, patch: Partial<ScanRow>) {
    setRows(prev => prev.map(r => r.input === input ? { ...r, ...patch } : r));
  }

  async function handlePodFile(input: string, file?: File) {
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('POD file must be < 5 MB');
      // Upload the POD to R2 (folder b2b_pod / b2c_pod) and store its ref (not base64).
      const { ref } = await uploadFile(file, orderType === 'B2B' ? 'b2b_pod' : 'b2c_pod');
      updateRow(input, { podFile: ref, podFileName: file.name });
    } catch (e: any) { toast.error(e?.response?.data?.message ?? e?.message ?? 'POD upload failed'); }
  }

  const needsPod = POD_REQUIRED_STATUSES.has(Number(statusId));
  const needsNdr = Number(statusId) === 9;

  const submit = useMutation({
    mutationFn: async () => {
      if (!statusId) throw new Error('Pick a tracking event');
      const valid = rows.filter(r => r.found.exists);
      if (valid.length === 0) throw new Error('Add at least one AWB found in database');
      if (needsPod) {
        const missing = valid.filter(r => !r.podFile || !r.receiverName?.trim());
        if (missing.length > 0) throw new Error(`POD + Receiver Name required for ${missing.length} AWB(s) (delivered/RTO status)`);
      }
      if (needsNdr && !ndrReason) throw new Error('Pick an NDR reason for Delivery Failed status');
      if (Number(statusId) === 23 && remarks.trim().length < 3) throw new Error('Enter a remark for the cancellation (at least 3 characters)');
      const payload: any = {
        statusId,
        remarks: remarks || undefined,
        chargedWeight: chargedWeight ? Number(chargedWeight) : undefined,
        lengthCm: lengthCm ? Number(lengthCm) : undefined,
        widthCm: widthCm ? Number(widthCm) : undefined,
        heightCm: heightCm ? Number(heightCm) : undefined,
        ndrReason: needsNdr && ndrReason ? Number(ndrReason) : undefined,
        assignedUserId: assignedUserId === '' ? undefined : Number(assignedUserId),
        latitude: lat ?? undefined, longitude: lng ?? undefined,
        city: city || undefined, state: state || undefined, pincode: pincode || undefined,
        perAwb: valid.map(r => ({
          // For a B2B child-box scan send the child AWB so the backend records weight/dims against
          // that exact box; otherwise send the master AWB / LR.
          awb: r.found.isChild ? (r.found.childAwb || r.input) : (r.found.awbNo || r.found.lrNo || r.input),
          podFile: r.podFile, receiverName: r.receiverName, receiverPhone: r.receiverPhone,
          chargedWeight: r.weight ? Number(r.weight) : undefined,
          lengthCm: r.lengthCm ? Number(r.lengthCm) : undefined,
          widthCm: r.widthCm ? Number(r.widthCm) : undefined,
          heightCm: r.heightCm ? Number(r.heightCm) : undefined,
        })),
      };
      return (await api.post('/tracking/bulk', payload)).data;
    },
    onSuccess: (data: any) => {
      const ok = data?.ok ?? 0; const fail = data?.failed ?? 0;
      // Each refused AWB comes back with its reason. They used to be counted and dropped, so a refused
      // POD correction read as "Saved 0 scans, 1 failed" with no way to know why.
      const errs: Array<{ awb: string; error: string }> = Array.isArray(data?.errors) ? data.errors : [];
      if (ok) toast.success(`Saved ${ok} scan${ok === 1 ? '' : 's'}`);
      if (fail) toast.error(`${fail} not saved: ` + errs.slice(0, 3).map((e) => `${e.awb} — ${e.error}`).join(' · ') + (errs.length > 3 ? ` (+${errs.length - 3} more)` : ''), { duration: 12000 });
      qc.invalidateQueries({ queryKey: ['tracking'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      if (ok) { onSuccess?.(); }
      if (!fail) onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Save failed'),
  });

  const sortedStatuses = useMemo(
    () => (statuses ?? []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [statuses],
  );

  return (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Scan Tracking Event *</label>
        <SearchableSelect
          value={statusId || ''}
          onChange={(v) => setStatusId(v === '' ? 0 : Number(v))}
          placeholder="Search & select event…"
          options={sortedStatuses.map(s => ({ value: s.id, label: `${s.name}${POD_REQUIRED_STATUSES.has(s.id) ? ' — POD required' : ''}` }))}
        />
        {needsPod && (
          <div className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
            This event requires POD + Receiver Details for each AWB.
          </div>
        )}
        {needsNdr && (
          <div className="mt-2">
            <label className="text-xs font-medium uppercase tracking-wide text-rose-700 mb-1 block">NDR Reason *</label>
            <select value={ndrReason} onChange={(e) => setNdrReason(e.target.value === '' ? '' : Number(e.target.value))}
              className="input w-full border-rose-200">
              <option value="">Pick NDR reason…</option>
              {(ndrStatuses ?? []).map((n: any) => (<option key={n.id} value={n.id}>{n.name}</option>))}
            </select>
            <div className="text-xs text-rose-600 mt-1">An NDR will be created automatically for these AWBs.</div>
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Assign User (optional)</label>
        <SearchableSelect
          value={assignedUserId}
          onChange={(v) => setAssignedUserId(v === '' ? '' : Number(v))}
          placeholder="Search & select user…"
          options={(users ?? []).map(u => ({ value: u.id, label: u.name ?? u.email, hint: (u as any).empCode ? String((u as any).empCode) : (u.mobile ?? undefined) }))}
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2 border-b border-slate-200">
          <button onClick={() => setTab('manual')}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 ${tab === 'manual' ? 'border-b-2 border-primary text-primary' : 'text-slate-500'}`}>
            <Keyboard size={14} /> Manual Entry
          </button>
          <button onClick={() => setTab('qr')}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 ${tab === 'qr' ? 'border-b-2 border-primary text-primary' : 'text-slate-500'}`}>
            <ScanLine size={14} /> QR Code Scanner
          </button>
        </div>

        {tab === 'manual' ? (
          <div>
            <div className="flex gap-2">
              <input value={awbInput} onChange={(e) => setAwbInput(toCommaList(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !lookupMut.isPending) { e.preventDefault(); lookupMut.mutate(awbInput); } }}
                placeholder={`Enter ${orderType === 'B2C' ? 'AWB' : 'LR or child AWB'} number(s) — spaces become commas, max 200`}
                className="input flex-1" />
              <button onClick={() => lookupMut.mutate(awbInput)} disabled={!awbInput.trim() || lookupMut.isPending}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
                {lookupMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} ADD
              </button>
            </div>
            <div className="text-xs text-slate-500 mt-1">Paste up to 200 AWB/LR numbers — spaces or line breaks between them become commas. We'll verify each in the database.</div>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
            QR scanner requires camera permission. <br />
            <span className="text-xs">(Tip: use device camera and paste decoded values above — comma-separated.)</span>
          </div>
        )}
      </div>

      {/* Scan Results */}
      {rows.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-2">Scan Results ({rows.length})</div>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">AWB / LR</th>
                  <th className="text-left px-3 py-2">Courier / Merchant</th>
                  <th className="text-left px-3 py-2">Weight (kg)</th>
                  <th className="text-left px-3 py-2">Dimensions L×W×H (cm)</th>
                  {needsPod && <th className="text-left px-3 py-2">POD *</th>}
                  {needsPod && <th className="text-left px-3 py-2">Receiver Details *</th>}
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const found = r.found;
                  // Show the id the user actually scanned first. For a B2B child-box AWB that's the
                  // child AWB (with a Box n/N badge); otherwise B2B → LR (docket), B2C → AWB.
                  const isChild = !!found.isChild;
                  const primaryId = isChild
                    ? (found.childAwb || found.input)
                    : orderType === 'B2B'
                      ? (found.lrNo || found.awbNo || found.input)
                      : (found.awbNo || found.lrNo || found.input);
                  const secondaryId = isChild ? found.lrNo : (orderType === 'B2B' ? found.awbNo : found.lrNo);
                  const secondaryLabel = isChild ? 'LR' : (orderType === 'B2B' ? 'Master AWB' : 'LR');
                  return (
                    <tr key={r.input} className={`border-t border-slate-100 ${!found.exists ? 'bg-rose-50' : ''}`}>
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-sm flex items-center gap-1.5">
                          {primaryId}
                          {isChild && found.boxNo && (
                            <span className="text-[10px] font-sans bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Box {found.boxNo}/{found.totalBoxes}</span>
                          )}
                        </div>
                        {found.exists && secondaryId && secondaryId !== primaryId && (
                          <div className="text-[11px] text-slate-400 font-mono">{secondaryLabel}: {secondaryId}</div>
                        )}
                        {found.exists ? (
                          <div className="text-[11px] text-slate-500">Order #{found.orderNo} · {found.orderType}</div>
                        ) : (
                          <div className="text-[11px] text-rose-600">Not found in database</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {found.exists ? (
                          <>
                            <div className="text-slate-900 font-medium">{found.courier?.name ?? '—'}</div>
                            <div className="text-slate-500">{found.merchant?.companyName ?? '—'}</div>
                          </>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {found.exists ? (
                          <input type="number" step="0.001" min="0" value={r.weight ?? ''}
                            onChange={(e) => updateRow(r.input, { weight: e.target.value })}
                            placeholder="kg" className="input input-num h-7 text-xs w-20" />
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {found.exists ? (
                          /* w-16 + `input-num`, not w-12: a carton is routinely 3 digits (120 × 100 × 90)
                             and the old 48px box with spinner arrows inside it clipped the third digit. */
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0" value={r.lengthCm ?? ''} onChange={(e) => updateRow(r.input, { lengthCm: e.target.value })} placeholder="L" className="input input-num h-7 text-xs w-16" />
                            <span className="text-slate-400">×</span>
                            <input type="number" step="0.01" min="0" value={r.widthCm ?? ''} onChange={(e) => updateRow(r.input, { widthCm: e.target.value })} placeholder="W" className="input input-num h-7 text-xs w-16" />
                            <span className="text-slate-400">×</span>
                            <input type="number" step="0.01" min="0" value={r.heightCm ?? ''} onChange={(e) => updateRow(r.input, { heightCm: e.target.value })} placeholder="H" className="input input-num h-7 text-xs w-16" />
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      {needsPod && (
                        <td className="px-3 py-2">
                          {found.exists ? (
                            <div className="relative">
                              <input type="file" accept="image/*,application/pdf"
                                onChange={(e) => handlePodFile(r.input, e.target.files?.[0])}
                                className="absolute inset-0 opacity-0 cursor-pointer" />
                              <div className={`text-xs border rounded px-2 py-1 inline-flex items-center gap-1 ${r.podFile ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200'}`}>
                                {r.podFile ? <><Check size={12} /> {r.podFileName ?? 'Uploaded'}</> : <><Upload size={12} /> Browse…</>}
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                      )}
                      {needsPod && (
                        <td className="px-3 py-2">
                          {found.exists ? (
                            <div className="space-y-1">
                              <input value={r.receiverName ?? ''} onChange={(e) => updateRow(r.input, { receiverName: e.target.value })}
                                placeholder="Receiver name" className="input h-7 text-xs w-full" />
                              <input value={r.receiverPhone ?? ''} onChange={(e) => updateRow(r.input, { receiverPhone: e.target.value })}
                                placeholder="Receiver phone (optional)" className="input h-7 text-xs w-full" />
                            </div>
                          ) : '—'}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <button onClick={() => removeRow(r.input)} className="text-rose-500 hover:text-rose-700"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Default Charged Weight (kg, optional)</label>
          <input value={chargedWeight} onChange={(e) => setChargedWeight(e.target.value)} type="number" step="0.001" className="input input-num w-full" />
          <div className="text-[11px] text-slate-400 mt-1">Applied to every AWB above that you leave blank.</div>
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Default Dimensions L × W × H (cm, optional)</label>
          <div className="flex items-center gap-1.5">
            <input value={lengthCm} onChange={(e) => setLengthCm(e.target.value)} type="number" step="0.01" min="0" placeholder="L" className="input input-num w-full" />
            <span className="text-slate-400">×</span>
            <input value={widthCm} onChange={(e) => setWidthCm(e.target.value)} type="number" step="0.01" min="0" placeholder="W" className="input input-num w-full" />
            <span className="text-slate-400">×</span>
            <input value={heightCm} onChange={(e) => setHeightCm(e.target.value)} type="number" step="0.01" min="0" placeholder="H" className="input input-num w-full" />
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Remarks (optional)</label>
        <div className="flex gap-2 items-center">
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input w-full" placeholder="Free-text notes — or tap the mic and speak" />
          <MicButton onText={(t) => setRemarks((p: string) => appendSpoken(p, t))} />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5"><MapPin size={12} /> Location (mandatory)</div>
          <button onClick={getLocation} disabled={geoLoading}
            className="text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1">
            {geoLoading ? <Loader2 size={12} className="animate-spin" /> : <Locate size={12} />} Get Location
          </button>
        </div>
        {geoError && <div className="text-xs text-rose-600 mb-2">{geoError}</div>}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Field label="Latitude" value={lat != null ? lat.toFixed(6) : ''} mono />
          <Field label="Longitude" value={lng != null ? lng.toFixed(6) : ''} mono />
          <Field label="Pincode" value={pincode} onChange={setPincode} editable />
          <Field label="City" value={city} onChange={setCity} editable />
          <Field label="State" value={state} onChange={setState} editable colSpan={2} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CANCEL</button>
        <button onClick={() => submit.mutate()} disabled={submit.isPending || rows.length === 0}
          className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : null} Save Scan{rows.length > 1 ? `s (${rows.filter(r => r.found.exists).length})` : ''}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, mono, onChange, editable, colSpan }: { label: string; value: string; mono?: boolean; onChange?: (v: string) => void; editable?: boolean; colSpan?: number }) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase text-slate-500 mb-0.5">{label}</div>
      {editable ? (
        <input value={value} onChange={(e) => onChange?.(e.target.value)} className={`input h-8 text-xs w-full ${mono ? 'font-mono' : ''}`} />
      ) : (
        <div className={`text-xs ${mono ? 'font-mono' : ''} text-slate-700 bg-white border border-slate-200 rounded px-2 py-1.5 min-h-[32px]`}>{value || '—'}</div>
      )}
    </div>
  );
}
